/**
 * Build a URL to the public host for previewing a page from the admin SPA
 * (#184). In docker compose / prod the admin SPA and the public renderer
 * share the same Express process (one image, one port), so we reuse the
 * admin's `protocol` and `port` from `window.location` and swap only the
 * hostname. In Vite dev mode the admin SPA runs on a different port
 * (5173) than the public renderer (the API on 3000); reusing 5173 there
 * routes the preview into Vite, which then bounces it back into the SPA
 * shell and renders an error boundary. `import.meta.env.DEV` lets us
 * steer the preview at the API port in that case.
 *
 * Returns `null` when there is no primary domain to preview against — the
 * caller hides the affordance in that case rather than rendering a broken
 * link.
 */

import type { DomainResponse } from "@ottopilot/shared";

/**
 * Port the API + public renderer listens on in Vite dev mode. Matches the
 * `PORT` env var the API server reads from `.env` (default 3000). If you
 * ever change that, change this too.
 */
const DEV_API_PORT = "3000";

export function findPrimaryDomain(
  domains: ReadonlyArray<DomainResponse> | undefined,
): DomainResponse | null {
  if (!domains) return null;
  return domains.find((d) => d.isPrimary) ?? null;
}

export function buildPublicUrl(hostname: string, path: string = "/"): string {
  const proto = typeof window !== "undefined" ? window.location.protocol : "http:";
  const adminPort = typeof window !== "undefined" ? window.location.port : "";
  const port = import.meta.env.DEV ? DEV_API_PORT : adminPort;
  const portSuffix = port ? `:${port}` : "";
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${proto}//${hostname}${portSuffix}${cleanPath}`;
}
